import { useAnalytics } from '@psyomics/ui-common';
import { useEffect } from 'react';
import { Model } from 'survey-react-ui';

import { useUpdateSurveyResponse } from '../../survey-responses/hooks/use-update-survey-response';

import { createSurveyEventHandlers } from './survey-event-handlers';
import { useDebouncedSurveyUpdate } from './use-debounced-survey-update';
import { useSurveyUpdateQueue } from './use-survey-update-queue';

type SurveyData = Record<string, unknown>;

type UseSurveyHandlerProps = {
  invitePublicId: string;
  organisationCode?: string | null;
  ready: boolean;
  reviewMode: boolean;
  serviceCode?: string | null;
  setSurveyResults: (data: SurveyData) => void;
  surveyModel: Model;
  surveyResponseId: string;
  surveyResponseUpdatedAt?: Date;
  updateSurveyAnswers: ReturnType<typeof useUpdateSurveyResponse>[1];
};

const useSurveyHandlers = ({
  invitePublicId,
  organisationCode,
  ready,
  reviewMode,
  serviceCode,
  setSurveyResults,
  surveyModel,
  surveyResponseId,
  surveyResponseUpdatedAt,
  updateSurveyAnswers,
}: UseSurveyHandlerProps) => {
  const analytics = useAnalytics();

  const enqueueUpdateSurvey = useSurveyUpdateQueue({
    ready,
    reviewMode,
    setSurveyResults,
    surveyResponseId,
    surveyResponseUpdatedAt,
    updateSurveyAnswers,
  });
  const updateSurvey = useDebouncedSurveyUpdate(enqueueUpdateSurvey);

  useEffect(() => {
    const handlers = createSurveyEventHandlers({
      analytics,
      invitePublicId,
      organisationCode,
      serviceCode,
      updateSurvey,
    });
    surveyModel.onCurrentPageChanged.add(handlers.onPageChange);
    surveyModel.onComplete.add(handlers.onComplete);
    surveyModel.onValueChanged.add(handlers.onValueChange);
    return () => {
      surveyModel.onValueChanged.clear();
      surveyModel.onCurrentPageChanged.remove(handlers.onPageChange);
      surveyModel.onComplete.clear();
    };
  }, [analytics, invitePublicId, organisationCode, serviceCode, surveyModel, updateSurvey]);
};

export { useSurveyHandlers };
